import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';

export default function Settings() {
  const [form, setForm] = useState({
    office_latitude: '',
    office_longitude: '',
    allowed_radius_meters: 100,
    official_start_time: '08:00',
    late_threshold_time: '09:00',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings').then(res => {
      if (res.data.settings) {
        const s = res.data.settings;
        setForm({
          office_latitude: s.office_latitude ?? '',
          office_longitude: s.office_longitude ?? '',
          allowed_radius_meters: s.allowed_radius_meters ?? 100,
          official_start_time: s.official_start_time?.slice(0, 5) ?? '08:00',
          late_threshold_time: s.late_threshold_time?.slice(0, 5) ?? '09:00',
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function useMyLocation() {
    navigator.geolocation.getCurrentPosition(pos => {
      setForm(f => ({
        ...f,
        office_latitude: pos.coords.latitude.toFixed(7),
        office_longitude: pos.coords.longitude.toFixed(7),
      }));
      toast.success('Location set to your current position');
    }, () => toast.error('Location access denied'));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/settings', form);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Layout><div style={{ textAlign: 'center', padding: '3rem' }}><span className="spinner spinner-dark" /></div></Layout>;

  return (
    <Layout>
      <div className="page-header">
        <h1>Settings</h1>
        <p>Configure office location, attendance radius, and time rules</p>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: 600 }}>
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>📍 Office Location</h2>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Latitude</label>
              <input className="form-input" type="number" step="any" placeholder="e.g. 6.5244" value={form.office_latitude}
                onChange={e => setForm({ ...form, office_latitude: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Longitude</label>
              <input className="form-input" type="number" step="any" placeholder="e.g. 3.3792" value={form.office_longitude}
                onChange={e => setForm({ ...form, office_longitude: e.target.value })} />
            </div>
          </div>
          <button type="button" className="btn btn-outline btn-sm" onClick={useMyLocation}>
            📍 Use My Current Location
          </button>
          <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
            Set to 0,0 to disable radius validation (allow sign-in from anywhere).
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>📏 Attendance Rules</h2>
          <div className="form-group">
            <label className="form-label">Allowed Radius (meters)</label>
            <input className="form-input" type="number" min="10" max="10000" value={form.allowed_radius_meters}
              onChange={e => setForm({ ...form, allowed_radius_meters: e.target.value })} />
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.25rem' }}>Staff must be within this radius to mark attendance.</p>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>⏰ Time Settings</h2>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Official Start Time</label>
              <input className="form-input" type="time" value={form.official_start_time}
                onChange={e => setForm({ ...form, official_start_time: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Late Threshold Time</label>
              <input className="form-input" type="time" value={form.late_threshold_time}
                onChange={e => setForm({ ...form, late_threshold_time: e.target.value })} />
              <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.25rem' }}>Staff signing in after this time will be marked as <strong>late</strong>.</p>
            </div>
          </div>
        </div>

        <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
          {saving ? <><span className="spinner" /> Saving...</> : 'Save Settings'}
        </button>
      </form>
    </Layout>
  );
}
